import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import type { CustomField } from '@sales-center/common'

import { useOpportunityCustomFieldSettings } from '@user/query/useOpportunityCustomFieldSettings'
import {
  selectAllCustomFields,
  useGetCustomFieldsOnMount,
} from '@user/store/customFields'
import type { ICustomFieldFormSetting } from '@user/store/entityFormSettings'

export function useData(missingFieldIds: number[]) {
  const customFieldsLoading = useGetCustomFieldsOnMount()
  const allCustomFields = useSelector(selectAllCustomFields)

  const { data: opportunitySettings, isLoading: settingsLoading } =
    useOpportunityCustomFieldSettings(true)

  const customSettings = useMemo<ICustomFieldFormSetting[]>(
    () =>
      (opportunitySettings ?? []).filter((setting) =>
        missingFieldIds.includes(setting.customFieldId),
      ),
    [missingFieldIds, opportunitySettings],
  )

  const settingByFieldId = useMemo(
    () =>
      new Map(
        customSettings.map((setting) => [setting.customFieldId, setting]),
      ),
    [customSettings],
  )

  const missingFields = useMemo<CustomField[]>(
    () =>
      allCustomFields
        .filter((field) => settingByFieldId.has(field.id))
        .sort(
          (a, b) =>
            (settingByFieldId.get(a.id)?.order ?? 0) -
            (settingByFieldId.get(b.id)?.order ?? 0),
        ),
    [allCustomFields, settingByFieldId],
  )

  return {
    customSettings,
    loading: customFieldsLoading || settingsLoading,
    missingFields,
    settingByFieldId,
  } as const
}

export type UseData = ReturnType<typeof useData>
